import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DataTable, DefinitionList, ErrorState, Loading, Metric,
} from '../../components/ui.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { date, dateNumeric, humanise, money, number, phoneWithCountryCode } from '../../lib/format.js';
import { useBrokerModeration } from './moderation.jsx';
import { useCreditAdjustment } from '../credits/adjust.jsx';
import { useAccountVisibility } from '../users/visibility.jsx';
import { DocumentPreviews } from '../../components/DocumentPreviews.jsx';
import { VerificationDetails } from './VerificationDetails.jsx';
import { DealsCard, EnquiriesCard, LeadsCard } from '../records/RecordCards.jsx';

/** Every area, spelled out — this screen has the room the table did not. */
function areaLines(areasServed = []) {
  return areasServed.map((area) => {
    const place = [area.city, area.state, area.country].filter(Boolean).join(', ');
    return area.localities?.length ? `${place} — ${area.localities.join(', ')}` : place;
  });
}

/**
 * One agency, in full.
 *
 * The list answers "which brokers need attention"; this answers "what is this
 * one, and should we still be carrying them". Each section is its own query so
 * an admin who can see brokers but not their inventory still gets the profile
 * rather than an error.
 */
export default function BrokerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const canModerate = can(PERMISSIONS.BROKER_MODERATE);
  // The same permission the account page asks for.
  const canHide = can(PERMISSIONS.USER_SUSPEND);
  const visibility = useAccountVisibility();
  const canSeeListings = can(PERMISSIONS.BROKER_PROPERTY_VIEW);
  const canSeeLedger = can(PERMISSIONS.FINANCE_VIEW);
  const canAdjustCredits = can(PERMISSIONS.CREDIT_ADJUST);

  const credits = useCreditAdjustment();

  const moderation = useBrokerModeration({ onDeleted: () => navigate('/brokers') });

  const query = useQuery({
    queryKey: ['admin-broker', id],
    queryFn: () => api.broker.broker(id),
  });

  const listings = useQuery({
    queryKey: ['admin-broker-listings', id],
    queryFn: () => api.broker.properties({ broker: id, pageSize: 10 }),
    enabled: Boolean(canSeeListings),
  });

  const ledger = useQuery({
    queryKey: ['admin-broker-ledger', query.data?.user?._id],
    queryFn: () => api.credits.ledger({ user: query.data.user._id, pageSize: 10 }),
    enabled: Boolean(canSeeLedger && query.data?.user?._id),
  });

  if (query.isLoading) {
    return (
      <Page title="Broker">
        <Loading label="Loading broker" />
      </Page>
    );
  }

  if (query.isError) {
    return (
      <Page title="Broker" actions={<BackToBrokers />}>
        <ErrorState error={query.error} onRetry={query.refetch} title="Could not load this broker" />
      </Page>
    );
  }

  const broker = query.data;
  const isSuspended = broker.status === 'suspended';
  const isVerified = broker.verification?.status === 'verified';

  const listingColumns = [
    {
      key: 'title',
      header: 'Listing',
      render: (property) => (
        <div>
          <div className="table__primary">{property.title}</div>
          <div className="table__muted">
            {[property.address?.locality, property.address?.city].filter(Boolean).join(', ')}
          </div>
        </div>
      ),
    },
    {
      key: 'beds',
      header: 'Beds',
      className: 'table__numeric',
      render: (property) => number(property.bedrooms),
    },
    {
      key: 'rent',
      header: 'Rent',
      className: 'table__numeric',
      render: (property) => (
        <span className="nowrap">
          {money(property.rentMinor)}
          <span className="muted">/mo</span>
        </span>
      ),
    },
    {
      key: 'verification',
      header: 'Verification',
      render: (property) => <Badge status={property.verification?.status} />,
    },
    { key: 'status', header: 'Status', render: (property) => <Badge status={property.status} /> },
  ];

  const ledgerColumns = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="table__muted nowrap">{dateNumeric(row.createdAt)}</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => (
        <div>
          <div>{humanise(row.type)}</div>
          <div className="table__muted truncate">{row.description}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Credits',
      className: 'table__numeric',
      render: (row) => (
        <span className={`nowrap ${row.amount > 0 ? 'credits--in' : 'credits--out'}`}>
          {row.amount > 0 ? '+' : '−'}
          {number(Math.abs(row.amount))}
        </span>
      ),
    },
    {
      key: 'balance',
      header: 'Balance after',
      className: 'table__numeric',
      render: (row) => <span className="table__muted">{number(row.balanceAfter)}</span>,
    },
  ];

  return (
    <Page
      title={broker.businessName}
      subtitle={
        broker.registrationNumber
          ? `Registration ${broker.registrationNumber}`
          : 'No registration number on file'
      }
      actions={
        <>
          <BackToBrokers />
          {canModerate && !isVerified ? (
            <Button variant="primary" onClick={() => moderation.ask(broker, 'approve')}>
              Verify broker
            </Button>
          ) : null}
          {/* Only when there is a submission to reject.
              An agency that has never sent anything has nothing to turn
              down, and offering it invited an admin to reject a broker for
              not having applied yet. */}
          {canModerate && broker.verification?.status === 'pending' ? (
            <Button variant="outline" onClick={() => moderation.ask(broker, 'reject')}>
              Reject verification
            </Button>
          ) : null}
          {canModerate ? (
            <Button
              variant={isSuspended ? 'primary' : 'danger'}
              onClick={() => moderation.ask(broker, isSuspended ? 'reinstate' : 'suspend')}
            >
              {isSuspended ? 'Reinstate' : 'Suspend'}
            </Button>
          ) : null}
          {/* Hidden or shown as the account behind the agency, which is
              where the setting lives — asked from here, so an agency is
              managed on its own page. */}
          {canHide && broker.user?._id ? (
            <Button
              variant="outline"
              onClick={() =>
                visibility.ask({
                  userId: broker.user._id,
                  name: broker.businessName,
                  hidden: Boolean(broker.user.hiddenFromPublic),
                })
              }
            >
              {broker.user.hiddenFromPublic ? 'Show to real users' : 'Hide from real users'}
            </Button>
          ) : null}
          {canAdjustCredits ? (
            <Button
              variant="outline"
              onClick={() => credits.ask({ brokerId: id, name: broker.businessName })}
            >
              Adjust credits
            </Button>
          ) : null}
          {canModerate ? (
            <Button variant="ghost" onClick={() => moderation.ask(broker, 'delete')}>
              Delete
            </Button>
          ) : null}
        </>
      }
    >
      <div className="stack">
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Badge status={broker.verification?.status} />
          <Badge status={broker.status} />
          <Badge tone="info" label={`${number(broker.user?.creditBalance || 0)} credits`} />
          {broker.user?.hiddenFromPublic ? <Badge tone="warning" label="Hidden from real users" /> : null}
        </div>

        <div className="metric-grid">
          <Metric label="Live listings" value={number(broker.stats?.activeListings || 0)} />
          <Metric label="Leads unlocked" value={number(broker.stats?.leadsUnlocked || 0)} />
          <Metric
            label="Credit balance"
            value={number(broker.user?.creditBalance || 0)}
            caption="One wallet, on their account"
          />
        </div>

        <div className="two-column">
          <div className="stack">
            <Card
              title="Verification"
              subtitle="What the agency is checked on depends on how it works: RERA, another registration, or an independent broker’s ID and references."
            >
              <DefinitionList
                items={[
                  { label: 'Status', value: humanise(broker.verification?.status) },
                  {
                    label: 'Submitted',
                    value: broker.verification?.submittedAt ? date(broker.verification.submittedAt) : null,
                  },
                  {
                    label: 'Reviewed',
                    value: broker.verification?.reviewedAt ? date(broker.verification.reviewedAt) : null,
                  },
                  { label: 'Documents', value: broker.verification?.documents?.length || null },
                  { label: 'Rejection reason', value: broker.verification?.rejectionReason },
                ]}
              />
              <VerificationDetails broker={broker} canModerate={canModerate} />
              <DocumentPreviews documents={broker.verification?.documents} />

              {isSuspended ? (
                <>
                  <p className="muted" style={{ marginTop: 'var(--space-5)', fontSize: 13 }}>
                    This account is suspended. Their listings are not being shown.
                  </p>
                  <DefinitionList
                    items={[
                      {
                        label: 'Suspended',
                        value: broker.suspension?.suspendedAt ? date(broker.suspension.suspendedAt) : null,
                      },
                      { label: 'Reason', value: broker.suspension?.reason },
                    ]}
                  />
                </>
              ) : null}
            </Card>

            {canSeeListings ? (
              <Card
                title="Listings"
                subtitle={
                  listings.data?.meta?.total
                    ? `${number(listings.data.meta.total)} in total, ten most recent shown.`
                    : 'What this agency currently has on the market.'
                }
                bodyless
              >
                <DataTable
                  columns={listingColumns}
                  rows={listings.data?.items || []}
                  query={listings}
                  empty={{
                    title: 'No listings',
                    body: 'This agency has not put anything on the market yet.',
                  }}
                />
              </Card>
            ) : null}

            {can(PERMISSIONS.LEAD_VIEW) ? (
              <>
                <EnquiriesCard brokerId={id} />
                <LeadsCard brokerId={id} />
                {can(PERMISSIONS.DEAL_VIEW) ? <DealsCard brokerId={id} /> : null}
              </>
            ) : null}

            {canSeeLedger ? (
              <Card
                title="Credits"
                subtitle="What this agency has spent and topped up, newest first."
                bodyless
              >
                <DataTable
                  columns={ledgerColumns}
                  rows={ledger.data?.items || []}
                  query={ledger}
                  empty={{
                    title: 'Nothing spent yet',
                    body: 'This agency has not put an ad up or unlocked a lead.',
                  }}
                />
              </Card>
            ) : null}
          </div>

          <div className="stack">
            <Card title="Contact">
              <DefinitionList
                items={[
                  { label: 'Account', value: broker.user?.name },
                  { label: 'Phone', value: phoneWithCountryCode(broker.phone) },
                  {
                    label: 'Account phone',
                    value:
                      broker.user?.phone && broker.user.phone !== broker.phone
                        ? phoneWithCountryCode(broker.user.phone)
                        : null,
                  },
                  { label: 'Joined', value: dateNumeric(broker.createdAt) },
                  { label: 'Last updated', value: dateNumeric(broker.updatedAt) },
                ]}
              />
            </Card>

            <Card title="Where they work">
              {areaLines(broker.areasServed).length ? (
                <ul className="plain-list">
                  {areaLines(broker.areasServed).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No areas set. They will not be matched to any requirement.</p>
              )}

              <DefinitionList
                items={[
                  {
                    label: 'Service radius',
                    value: broker.serviceRadiusKm ? `${broker.serviceRadiusKm} km` : null,
                  },
                ]}
              />
            </Card>

            {broker.about ? (
              <Card title="About">
                <p style={{ fontSize: 14, lineHeight: 1.6 }}>{broker.about}</p>
              </Card>
            ) : null}

            <Card title="Wallet">
              <DefinitionList
                items={[
                  { label: 'Balance', value: `${number(broker.user?.creditBalance || 0)} credits` },
                  {
                    label: 'Rating',
                    value: broker.stats?.ratingCount
                      ? `${broker.stats.ratingAverage} from ${number(broker.stats.ratingCount)}`
                      : null,
                  },
                ]}
              />
            </Card>
          </div>
        </div>
      </div>

      {moderation.dialog}
      {credits.dialog}
      {visibility.dialog}
    </Page>
  );
}

function BackToBrokers() {
  return (
    <Link to="/brokers">
      <Button variant="ghost">All brokers</Button>
    </Link>
  );
}
